import React, { useRef, useState } from 'react';
import useComprehensiveTopNav from 'hooks/useComprehensiveTopNav';
import { Typography } from 'lib/components';
import classes from './index.module.scss';
import Image from 'lib/components/Image';
import { useHistory } from 'react-router-dom';
import ChatScreen from './ChatScreen';
import Overview from './Overview';
import Log from './Log';
import Invoices from './Invoices';
import { useWindowResize } from 'hooks/useWindowResize';

/**
 * ActiveJobScreen component
 * Displays detailed information about an active job with tabbed navigation
 * Includes Overview, Log, and Invoices tabs, plus a Chat feature
 *
 * @returns {JSX.Element} - Rendered component
 */
const ActiveJobScreen = () => {
	// Reference to the container element for potential DOM operations
	const containerRef = useRef<HTMLDivElement>(null);
	// Navigation helper
	const { push } = useHistory();
	// State for the currently selected tab
	const [tabIndex, setTabIndex] = useState('0');
	// State for toggling the chat screen visibility
	const [showChatScreen, setShowChatScreen] = useState(false);
	// Responsive design helpers
	const { isSmallerDevice, isSmallDevice } = useWindowResize();
	// Commented out chat popup state
	// const [chatPopupOpen, setChatPopupOpen] = useState(true);

	/**
	 * Tab configuration with components to render for each tab
	 * Each tab has an ID, label, and associated component
	 */
	const tabs = [
		{ id: '0', label: 'Overview', component: <Overview /> },
		{ id: '1', label: 'Log', component: <Log /> },
		{ id: '2', label: 'Invoices', component: <Invoices /> },
	];

	// Set up top navigation with title
	useComprehensiveTopNav('Jobs');

	return (
		<>
			{/* Commented out chat popup - could be used for a floating chat window */}
			{/* <ChatPopup isOpen={chatPopupOpen} onClose={() => setChatPopupOpen(false)} /> */}

			{/* Main container for the active job screen */}
			<div className={classes['dispatch-view-container']} ref={containerRef}>
				{/* Tab navigation and action buttons */}
				<div
					className={classes['tabs']}
					style={{
						padding: isSmallDevice ? '0 1rem' : undefined,
					}}
				>
					{/* Map through tabs and render tab buttons */}
					{tabs.map(({ id, label }) => (
						<button
							className={classes['tab']}
							key={id}
							style={{
								// Highlight the currently selected tab
								borderColor: tabIndex === id ? ' #0CB9C3' : 'transparent',
							}}
							onClick={() => setTabIndex(id)}
						>
							<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>{label}</Typography>
						</button>
					))}

					{/* Action buttons container */}
					<div
						className={classes['row']}
						style={{
							marginLeft: 'auto',
							paddingBottom: '0.5rem',
						}}
					>
						{/* Chat button with notification count */}
						<button
							className={classes['row']}
							style={{
								marginTop: '0.5rem',
								marginRight: '1.5rem',
							}}
							onClick={() => setShowChatScreen(!showChatScreen)}
						>
							<Image
								src="icChat"
								style={{
									marginRight: '0.5rem',
								}}
							/>
							<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Chat</Typography>
							{/* Notification badge showing unread message count */}
							<div className={classes['chat-notifications-number']}>
								<p>4</p>
							</div>
						</button>

						{/* Export button */}
						<button
							className={classes['row']}
							style={{
								marginRight: '1.5rem',
								marginTop: '0.5rem',
							}}
						>
							<Image src="icExportBlack" />
							<Typography
								variant={isSmallerDevice ? 'h5' : 'h6'}
								style={{
									marginLeft: '0.5rem',
								}}
							>
								Export
							</Typography>
						</button>

						{/* Edit button - navigates to job edit screen */}
						<button
							className={classes['row']}
							style={{
								marginTop: '0.5rem',
							}}
							onClick={() => push('/jobs/edit/hdyeh')}
						>
							<Image src="icEdit" />
							<Typography
								variant={isSmallerDevice ? 'h5' : 'h6'}
								style={{
									marginLeft: '0.5rem',
								}}
							>
								Edit
							</Typography>
						</button>
					</div>
				</div>

				{/* Content area - shows either chat screen or the selected tab component */}
				{showChatScreen ? <ChatScreen /> : <>{tabs[+tabIndex].component}</>}
			</div>
		</>
	);
};

export default ActiveJobScreen;
